/** Permission keys and labels for the user permissions page (order preserved). */

/** @param {{ items?: object[], subsections?: { items: object[] }[] }} group */
export function flattenGroupItems(group) {
  if (group.subsections?.length) {
    return group.subsections.flatMap((section) => section.items || []);
  }
  return group.items || [];
}

export const PERMISSION_GROUPS = [
  {
    id: "global",
    title: "Global",
    description: "Core modules, events, and cross-cutting access",
    items: [
      { key: "manage_tasks", label: "Can Edit Tasks?" },
      { key: "accounts", label: "Can view Accounts?" },
      { key: "agents", label: "Can view Agents?" },
      { key: "reports", label: "Can view Reports?" },
      { key: "show_events", label: "Can view Events?" },
      { key: "expo-2022", label: "Can view All Expo?" },
      { key: "expo-consultant", label: "Can view Consultant List?" },
      { key: "floor_manager", label: "Make Floor Manager for Events?" },
      { key: "enable_app_priority_prompt", label: "Enable App Priority Prompt?" },
      { key: "show_commission_notes", label: "Can view Commissions Activity?" },
      { key: "can_export_data", label: "Can Export Data?" },
      { key: "can_archive_record", label: "Can Archive Record?" },
      { key: "can_restore_archived_record", label: "Can Restore Archived Record?" },
      { key: "can_delete_own_draft", label: "Can Delete Own Draft?" },
      { key: "can_delete_department_draft", label: "Can Delete Department Draft?" },
      {
        key: "can_permanently_delete_record",
        label: "Can Permanently Delete Record? (Super Admin / highly privileged)",
      },
      {
        key: "can_delete",
        label: "Legacy: Can Delete Entries and Export file? (deprecated — use keys above)",
      },
    ],
  },
  {
    id: "sop_policies",
    title: "SOP & Policies",
    description: "Standard operating procedures and policy documents",
    items: [
      { key: "view_sop_and_policies", label: "Can View SOPs and Policies?" },
      { key: "review_sop_and_policies", label: "Can Review SOPs and Policies?" },
      { key: "upload_sop_and_policies", label: "Can Upload SOPs and Policies?" },
    ],
  },
  {
    id: "company_forms",
    title: "Company Documents",
    description: "Shared terms, service agreements, and approved company documents",
    items: [
      {
        key: "upload_company_forms",
        label:
          "Can upload company documents? (includes edit, delete, and bulk delete for all document types)",
      },
      {
        key: "approve_company_forms",
        label:
          "Can approve company documents? (includes upload, approve/reject, bulk approve, edit, delete, and bulk delete for all document types)",
      },
    ],
  },
  {
    id: "leads",
    title: "Leads",
    description: "Lead lists, website intake, and campaign leads",
    items: [
      { key: "leads", label: "Can view Leads?" },
      { key: "assign_website_leads", label: "Can Assign Website Leads?" },
      { key: "view_campaign_leads", label: "Can View Campaign Leads?" },
      { key: "edit_campaign_leads", label: "Can Edit Campaign Leads?" },
      { key: "delete_campaign_leads", label: "Can Manage Campaign Leads?" },
    ],
  },
  {
    id: "clients",
    title: "Clients",
    description: "Client records and external client access",
    items: [
      { key: "clients", label: "Can view Clients?" },
      { key: "external_clients", label: "Can view External Clients?" },
    ],
  },
  {
    id: "followups",
    title: "Followups",
    description: "Team-wide and personal follow-up visibility",
    items: [
      { key: "all_followups", label: "Can view Followups?" },
      { key: "all_clients_followups", label: "Can view all Clients Followups?" },
      { key: "all_mig_followups", label: "Can view all Migration Followups?" },
      { key: "all_edu_followups", label: "Can view all Education Followups?" },
      { key: "all_providers_followups", label: "Can view all Providers Followups?" },
      {
        key: "all_invoice_client_followups",
        label: "Can view all Client Invoice Followups?",
      },
      {
        key: "all_invoice_commission_followups",
        label: "Can view all Commission Invoice Followups?",
      },
      { key: "biometrics", label: "Can view Biometrics Followups?" },
      { key: "health_examination", label: "Can view Health Examination Followups?" },
      { key: "my_clients_followups", label: "Can view My Clients Followups?" },
      { key: "my_mig_followups", label: "Can view My Migration Followups?" },
      { key: "my_edu_followups", label: "Can view My Education Followups?" },
      { key: "my_providers_followups", label: "Can view My Providers Followups?" },
      {
        key: "my_invoice_client_followups",
        label: "Can view My Client Invoice Followups?",
      },
      {
        key: "my_invoice_commission_followups",
        label: "Can view My Commission Invoice Followups?",
      },
    ],
  },
  {
    id: "providers",
    title: "Providers",
    description: "Provider records, contacts, and commissions",
    items: [
      { key: "providers", label: "Can view Providers?" },
      { key: "can_edit_provider", label: "Can edit Providers?" },
      { key: "providers_accounts", label: "Can view Providers Accounts Contact?" },
      { key: "providers_admissions", label: "Can view Providers Admission Contact?" },
      { key: "providers_marketing", label: "Can view Providers Marketing Contact?" },
      { key: "providers_owner", label: "Can view Providers Owner Contact?" },
      { key: "providers_other", label: "Can view Providers Other Contact?" },
      { key: "providers_commissions", label: "Can view Providers Commissions?" },
      {
        key: "show_application_commission",
        label: "Can view Commission Button in Applications?",
      },
    ],
  },
  {
    id: "appointments",
    title: "Appointments",
    description: "Calendar and appointment visibility",
    items: [
      { key: "appointments", label: "Can view Appointments?" },
      { key: "viewappointments", label: "Can view all Appointments?" },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    description: "Administration, audit, and configuration",
    items: [
      { key: "settings_page", label: "Can view Settings Page?" },
      { key: "loginhistory", label: "Can view Login History?" },
      { key: "employeeactivity", label: "Can view Employee Activity?" },
      { key: "ip_address_page", label: "Can view IP Address?" },
      { key: "permissions_page", label: "Can view Permissions?" },
      { key: "categories", label: "Can view Categories?" },
      { key: "manage_teams", label: "Can view Manage Teams?" },
      { key: "manage_checklist", label: "Can view Manage Checklist?" },
      {
        key: "website_api_settings",
        label: "Can manage Website API integrations (rotate keys)?",
      },
      {
        key: "website_api_view_logs",
        label: "Can view Website API integrations (read-only)?",
      },
    ],
  },
  {
    id: "desktop",
    title: "Desktop",
    description: "Desktop dashboard widgets and status cards",
    items: [
      { key: "desk_puser", label: "Can view Per User Details?" },
      { key: "desk_rfi", label: "Can view Desktop RFI Details?" },
      { key: "desk_nj", label: "Can view Desktop NJ Details?" },
      { key: "desk_noicc", label: "Can view Desktop NOICC?" },
    ],
  },
  {
    id: "migration",
    title: "Migration",
    description: "Migration application totals on the dashboard",
    items: [
      { key: "desk_migration", label: "Can view Total Migrations?" },
    ],
  },
  {
    id: "education",
    title: "Education",
    description: "Education totals and desktop education widgets",
    items: [
      { key: "desk_education", label: "Can view Total Educations?" },
    ],
  },
  {
    id: "finance",
    title: "Finance",
    description: "Invoices, costs, and financial notes",
    items: [
      {
        key: "finance_notes",
        label: "Add Financial Notes in clients and their applications",
      },
      { key: "finance", label: "Can view Finance Module" },
      { key: "view_application_invoices", label: "Can view application invoices?" },
      {
        key: "view_commission_invoices",
        label:
          "Can view commission invoices (Finance tab + Commissionable Students)?",
      },
      {
        key: "view_commission_module",
        label: "Can view commission module (Commissions workspace)?",
      },
      { key: "professionalCost", label: "Can view Professtional Cost Page" },
      { key: "awaiting_approval", label: "Can open Awaiting Approval invoice tab?" },
      { key: "edit_invoice", label: "Can edit invoice after draft?" },
      { key: "delete_invoice", label: "Can delete invoice?" },
      {
        key: "remove_from_commissionable",
        label:
          "Can remove students from Commissionable Students (move to General Report)?",
      },
      {
        key: "bulk_commission_mark_completed",
        label: "Bulk: mark commissionable students as completed?",
      },
      {
        key: "bulk_commission_discontinue",
        label: "Bulk: mark commissionable students as discontinued?",
      },
      {
        key: "bulk_commission_reactivate",
        label: "Bulk: restore discontinued students to active?",
      },
      {
        key: "bulk_commission_revert_completed",
        label: "Bulk: restore completed students to previous tab?",
      },
      {
        key: "bulk_commission_submit_final_review",
        label: "Bulk: send students for final review?",
      },
      {
        key: "bulk_commission_approve_final_review",
        label: "Bulk: approve and close final review?",
      },
      {
        key: "bulk_commission_return_final_review",
        label: "Bulk: return students from final review?",
      },
      {
        key: "bulk_commission_create_inquiry_batch",
        label: "Bulk: create provider inquiry batches?",
      },
    ],
  },
  {
    id: "hr",
    title: "HR Management",
    description: "People operations, attendance, and alerts",
    items: [
      {
        key: "manage_departments",
        label: "Manage Departments, Teams and Notifications",
      },
      {
        key: "manage_policies",
        label: "Can Manage Policies (Attendance, Leave, etc.)",
      },
      { key: "hr-management", label: "Can Access HR Managment Module" },
      { key: "manage_leaves", label: "Can View Manage Leaves Page?" },
      { key: "manage_roles", label: "Can View Manage Roles Page?" },
      { key: "can_create_alerts", label: "Can Create Alerts?" },
      { key: "manage_branches", label: "Can Manage Branch?" },
      { key: "show_attendance", label: "Can View Attendance Sheet?" },
      { key: "daily_report_remarks", label: "Can give Remarks on Daily Report" },
    ],
  },
];

export const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap((g) =>
  flattenGroupItems(g).map((i) => i.key),
);
